/**
 * A shortcut for `buildEditFormStateHandler` that uses the standard React `state` property and `setState`.
 * @param key The key in component state to use for form state
 * @param component The component instance
 */
export var buildEditFormStateHandlerForComponent = function (key, component) {
    return buildEditFormStateHandler(key, function () { return component.state; }, 
    // bind() is important: without it, React's setState ends up barfing because `this` is undefined. Oh JS...
    component.setState.bind(component));
};
/**
 * Build an EditFormStateHandler that manages updating a form's state as a property in component state.
 * @param key The key used to hold form state in the component state
 * @param getState A function that produces state, e.g. by accessing `this.state`
 * @param setState A function that behaves just like React's setState:
 *      it invokes the provided function to build a Pick of the component state, applies the Pick as a delta to the
 *      component state, and invokes the callback once the state change is applied.
 */
var buildEditFormStateHandler = function (key, getState, setState) {
    return function (updater, callback) {
        var stateFunc = function (oldState) {
            var origFormState = oldState[key];
            var newPartialFormState = updater(origFormState);
            // merge the new form state onto the old one
            var newFormState = Object.assign({}, origFormState, newPartialFormState);
            // Programmatically make an object (satisfying Pick<S, k>) with the field name `key` and value `value`.
            // `enumerable` = true is needed so that React's delta application can find the new value.
            return Object.defineProperty({}, key, { value: newFormState, enumerable: true });
        };
        setState(stateFunc, function () {
            if (callback) {
                callback((getState())[key]);
            }
        });
    };
};
// Build an state change handler function for a particular state holder type.
// The intent is to abstract over the similarities between RNA-FISH and IHC2 kits handling
// the state changes for the various component forms (amplifier, buffer, ...).
export function buildOnComponentStateChangeHandler(holderWrangler, stateHolderUpdater) {
    return function (newState, callback) {
        var updater = function (oldStateHolder) {
            // get the new partial of the child state
            var partial;
            if (typeof newState === "function") {
                partial = newState(holderWrangler.extractComponentState(oldStateHolder));
            }
            else {
                partial = newState;
            }
            // wrap it in the state holder
            var ampState = Object.assign({}, holderWrangler.extractComponentState(oldStateHolder), partial);
            return holderWrangler.buildHolderFromComponentState(ampState);
        };
        stateHolderUpdater(updater, callback);
    };
}
//# sourceMappingURL=FormState.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/kit/FormState.js
// module id = 33
// module chunks = 0